import { useParams } from "react-router";
import { errorMessage } from "../../shared/api/errors.ts";
import { deviceLabel, useDevices } from "../../shared/devices/devices.ts";
import {
  clockTime,
  messageDeliveryStatus,
  longDate,
  messageTypeLabel,
  senderName,
  useMessage,
  type Message,
} from "../../shared/messages/messages.ts";
import { MessageImage } from "../../shared/messages/MessageImage.tsx";
import { DeviceScreen } from "../../shared/device-screen/DeviceScreen.tsx";
import { useSession } from "../../shared/session/SessionProvider.tsx";
import { BottomSheet, useCloseSheet } from "../../shared/ui/BottomSheet/BottomSheet.tsx";
import { Button } from "../../shared/ui/Button/Button.tsx";
import { Spinner } from "../../shared/ui/Spinner/Spinner.tsx";
import styles from "./MessageDetailsSheet.module.css";

/**
 * 07.09 — «Информация о сообщении» (Figma 627:4753): шторка вложенным маршрутом поверх экрана с карточкой,
 * поэтому системная «назад» её закрывает. `closeTo` — куда вернуться, если шторку открыли по прямой ссылке.
 */
export function MessageDetailsSheet({ closeTo }: { closeTo: string }) {
  const { messageId = "" } = useParams();
  const close = useCloseSheet(closeTo);
  const message = useMessage(messageId);

  return (
    <BottomSheet open onClose={close} title="Информация о сообщении">
      {message.isPending ? (
        <div className={styles.placeholder} role="status" aria-label="Загрузка сообщения">
          <Spinner size={48} />
        </div>
      ) : message.isError ? (
        <div className={styles.placeholder}>
          <p className={styles.notice} role="alert">
            {errorMessage(message.error)}
          </p>
          <Button variant="secondary" onClick={() => void message.refetch()}>
            Повторить
          </Button>
        </div>
      ) : (
        <Details message={message.data} />
      )}
    </BottomSheet>
  );
}

function Details({ message }: { message: Message }) {
  const { user } = useSession();
  const device = useDevices().data?.find((item) => item.id === message.deviceId);
  const rows: [string, string][] = [
    ["Отправитель", senderName(message)],
    // Получатель сообщения — E-va; у чужой E-va — с именем владельца.
    ["Получатель", device ? deviceLabel(device, user?.id) : "E-va"],
    ["Дата отправки", longDate(message.createdAt)],
    ["Время", clockTime(message.createdAt)],
    ["Тип сообщения", messageTypeLabel(message)],
    ["Статус", messageDeliveryStatus(message).label],
    ["Лайки на открытке", String(message.likeCount ?? 0)],
  ];
  return (
    <>
      {/* В макете 07.09 содержимого нет; картинку с подписью добавляет спецификация (#27). */}
      {message.screenMediaUrl ? <div className={styles.screenPreview}><DeviceScreen image={message.screenMediaUrl} imageFit={message.imageFit} text={message.text} sender={message.showSender ? message.sender.displayName : null} /></div> : message.mediaUrl && (
        <figure className={styles.media}>
          <MessageImage className={styles.image} message={message} />
          {message.text && <figcaption className={styles.caption}>{message.text}</figcaption>}
        </figure>
      )}
      {message.queueStatus === "rendering" && <p className={styles.deliveryHint}>Фото загружается на открытке. Показ на экране ещё не подтверждён.</p>}
      {message.queueStatus === "pending" && <p className={styles.deliveryHint}>Сообщение ждёт своей очереди. Оно появится после окончания текущего показа и восстановления связи.</p>}
      <dl className={styles.details}>
        {rows.map(([label, value]) => (
          <div key={label} className={styles.row}>
            <dt className={styles.label}>{label}</dt>
            <dd className={styles.value}>{value}</dd>
          </div>
        ))}
      </dl>
    </>
  );
}
